import { useMutation } from '@apollo/client/react';
import { toast } from 'sonner';

import { AppCriticalityEnum } from 'src/graphql/schema';
import { AppUpdateDocument } from 'src/graphql/actions/apps/appUpdate.mutation';
import { getApiError, ClientError } from 'src/utils/errors';

import type { IAppQuery } from 'src/graphql/actions/apps/app.query';
import type { IOwnApplicationForm } from './ownApplicationSchema';

import { useAppLogoUpload } from './useAppLogoUpload';

export const useUpdateApp = (app: NonNullable<IAppQuery['app']>) => {
  const [updateAppMutation] = useMutation(AppUpdateDocument);
  const uploadAppLogo = useAppLogoUpload();

  return async (data: IOwnApplicationForm, successMessage: (name: string) => string) => {
    try {
      const logo = data.logo instanceof File ? await uploadAppLogo(data.logo) : undefined;

      const { data: result } = await updateAppMutation({
        variables: {
          input: {
            appId: app.id,
            appParams: {
              name: data.name,
              kind: data.kind,
              includedApps: data.includedApps,
              criticality: data.criticality as AppCriticalityEnum,
              industry: data.industry,
              softwareType: data.softwareType,
              jurisdiction: data.jurisdiction,
              description: data.description,
              logo,
              removeLogo: !data.logo && Boolean(app.logoUrl),
            },
          },
        },
      });

      if (!result?.appUpdate?.success) throw new ClientError(result?.appUpdate?.errors?.[0]);

      toast.success(successMessage(result.appUpdate.app?.name ?? data.name));
      return true;
    } catch (error) {
      getApiError(error, toast.error);
      return false;
    }
  };
};
